import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Arr } from "../../components/CTA";

/* ───────────────────────── Interactive planner ─────────────────────── */

const PHASE_DEFS = [
  {
    n: 1,
    name: "Train and configure",
    tone: "p1",
    activities:
      "Train models on the real document examples you gathered. Set the field set, the confidence threshold and the review routing.",
    answers: "Can this tool be taught your documents at all.",
    measure: "Nothing yet. This phase is setup.",
  },
  {
    n: 2,
    name: "Run on real volume",
    tone: "p2",
    activities:
      "Push your actual, ongoing document flow through the tool at close to real volume. Let the review loop run, and let your team make corrections.",
    answers: "How much human time does it remove, and is the output trustworthy.",
    measure:
      "Time per document against your baseline. The share of fields going to review. The error rate. Whether the review rate is trending down.",
  },
  {
    n: 3,
    name: "Measure and decide",
    tone: "p3",
    activities:
      "Compare results against your written baseline and success criterion. Look at the trend, not only the final snapshot. Make the call with the owner and the room you named.",
    answers: "Yes or no, and the number that justifies it.",
    measure: "The final comparison against baseline and criterion.",
  },
];

// returns [{ phase: 1|2|3, weeks: [n, ...] }, ...] for the given length
function phaseMap(len) {
  if (len === 4) return [{ phase: 1, weeks: [1] }, { phase: 2, weeks: [2, 3] }, { phase: 3, weeks: [4] }];
  if (len === 5) return [{ phase: 1, weeks: [1, 2] }, { phase: 2, weeks: [3, 4] }, { phase: 3, weeks: [5] }];
  return [{ phase: 1, weeks: [1, 2] }, { phase: 2, weeks: [3, 4, 5] }, { phase: 3, weeks: [6] }];
}

function formatDate(d) {
  if (!d) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function Planner() {
  const [length, setLength] = useState(6);
  const [docTypes, setDocTypes] = useState(2);
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [openPhase, setOpenPhase] = useState(1);
  const [checked, setChecked] = useState(() => new Set());

  const map = useMemo(() => phaseMap(length), [length]);

  const weeks = useMemo(() => {
    const arr = [];
    for (let w = 1; w <= length; w++) {
      let phase = 1;
      if (length === 4) phase = w === 1 ? 1 : w <= 3 ? 2 : 3;
      else if (length === 5) phase = w <= 2 ? 1 : w <= 4 ? 2 : 3;
      else phase = w <= 2 ? 1 : w <= 5 ? 2 : 3;
      let label = `Week ${w}`;
      if (startDate) {
        const base = new Date(startDate);
        if (!isNaN(base.getTime())) {
          const d = new Date(base);
          d.setDate(d.getDate() + (w - 1) * 7);
          label = formatDate(d);
        }
      }
      arr.push({ w, phase, label });
    }
    return arr;
  }, [length, startDate]);

  const endDateStr = useMemo(() => {
    if (!startDate) return null;
    const base = new Date(startDate);
    if (isNaN(base.getTime())) return null;
    const end = new Date(base);
    end.setDate(end.getDate() + length * 7 - 1);
    return end.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }, [startDate, length]);

  const startDateStr = useMemo(() => {
    if (!startDate) return null;
    const d = new Date(startDate);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }, [startDate]);

  const toggleCheck = (i) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const checklistItems = [
    `Pick your ${docTypes} document types: the ones that hurt most and are representative, not the easy ones.`,
    "Write your success criterion as a number, before the pilot starts.",
    "Measure your current baseline: time per document, rework rate, cost.",
    "Name one owner for the decision, and who is in the room for it.",
    `Gather real examples of those ${docTypes} document types, including the messy ones.`,
  ];

  return (
    <aside className="pln" aria-labelledby="pln-title" data-testid="pilot-planner">
      <header className="pln__head">
        <h3 id="pln-title" className="pln__title">Plan your own pilot</h3>
        <p className="pln__intro">
          Set the shape of your pilot and the planner lays out the weeks. Nothing is sent anywhere.
        </p>
      </header>

      {/* Controls */}
      <div className="pln__controls">
        <div className="pln__ctrl">
          <span className="pln__lab">Pilot length</span>
          <div className="pln__seg" role="radiogroup" aria-label="Pilot length">
            {[4, 5, 6].map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={length === n}
                className={`pln__seg-opt${length === n ? " is-on" : ""}`}
                onClick={() => setLength(n)}
                data-testid={`pln-length-${n}`}
              >
                {n} weeks
              </button>
            ))}
          </div>
        </div>
        <div className="pln__ctrl">
          <span className="pln__lab">Document types</span>
          <div className="pln__seg" role="radiogroup" aria-label="Document types">
            {[2, 3].map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={docTypes === n}
                className={`pln__seg-opt${docTypes === n ? " is-on" : ""}`}
                onClick={() => setDocTypes(n)}
                data-testid={`pln-docs-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
        <div className="pln__ctrl pln__ctrl--date">
          <label htmlFor="pln-start" className="pln__lab">Start date <span className="pln__opt">(optional)</span></label>
          <input
            id="pln-start"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="pln__date"
            data-testid="pln-start"
          />
        </div>
      </div>

      {/* Checklist */}
      <div className="pln__check">
        <h4 className="pln__sub">Before week one</h4>
        <ul className="pln__list">
          {checklistItems.map((t, i) => {
            const id = `chk-${i}`;
            const on = checked.has(i);
            return (
              <li key={i} className={`pln__chk${on ? " is-on" : ""}`}>
                <input
                  id={id}
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleCheck(i)}
                  data-testid={`pln-check-${i + 1}`}
                />
                <label htmlFor={id}>
                  <span className="pln__chk-box" aria-hidden="true">
                    <svg viewBox="0 0 14 14" fill="none"><path d="M3 7.5l2.4 2.4L11 4.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                  {t}
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Timeline */}
      <div className="pln__tl" aria-label="Week-by-week timeline">
        <div className="pln__weeks">
          {weeks.map((w) => (
            <div key={w.w} className={`pln__wk pln__wk--p${w.phase}`}>
              <span className="pln__wknum">{w.label}</span>
            </div>
          ))}
        </div>
        <div className="pln__phases">
          {map.map((p) => {
            const def = PHASE_DEFS[p.phase - 1];
            const span = p.weeks.length === 1 ? p.weeks[0] : `${p.weeks[0]}–${p.weeks[p.weeks.length - 1]}`;
            const isOpen = openPhase === p.phase;
            return (
              <div
                key={p.phase}
                className={`pln__phase pln__phase--${def.tone}${isOpen ? " is-open" : ""}`}
                style={{ flexBasis: `${(p.weeks.length / length) * 100}%` }}
              >
                <button
                  type="button"
                  className="pln__phase-row"
                  aria-expanded={isOpen}
                  aria-controls={`pln-phase-${p.phase}`}
                  onClick={() => setOpenPhase(isOpen ? 0 : p.phase)}
                  data-testid={`pln-phase-${p.phase}`}
                >
                  <span className="pln__phase-tag">Phase {def.n}</span>
                  <span className="pln__phase-name">{def.name}</span>
                  <span className="pln__phase-span">Week{p.weeks.length > 1 ? "s" : ""} {span}</span>
                  <svg className="pln__phase-caret" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div
                  id={`pln-phase-${p.phase}`}
                  className="pln__phase-detail"
                  role="region"
                  hidden={!isOpen}
                >
                  <div className="pln__phase-detail-inner">
                    <div><b>Activities.</b> {def.activities}</div>
                    <div><b>The question it answers.</b> {def.answers}</div>
                    <div><b>What to measure.</b> {def.measure}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <p className="pln__summary" data-testid="pln-summary">
        Your pilot: <b>{docTypes}</b> document types, <b>{length}</b> weeks, ending in a yes or no decision.
        {startDateStr && endDateStr && (
          <> Runs <b>{startDateStr}</b> to <b>{endDateStr}</b>.</>
        )}
      </p>

      <p className="pln__foot">
        A pilot of this shape gives you a trajectory and a clear decision, not a finished production system. Integration and scale are a separate exercise that comes after a yes.
      </p>
    </aside>
  );
}

/* ────────────────────────────── Page ────────────────────────────── */

export default function IdpPilotPlaybook() {
  return (
    <article className="article">
      <header className="article__head">
        <div className="container container--narrow">
          <nav className="crumb" aria-label="Breadcrumb">
            <Link to="/">Docket</Link> / <Link to="/insights">Insights</Link> / How to run an IDP pilot that ends in a decision
          </nav>
          <p className="article__eyebrow">Sales playbook &nbsp;·&nbsp; October 2025 &nbsp;·&nbsp; 6 min read</p>
          <h1 className="article__title">How to run an IDP pilot that ends in a decision</h1>
          <p className="article__standfirst">
            Most IDP pilots end in a shrug. A good one is built, before it starts, to end in a clear yes or no. Here is the shape, week by week.
          </p>
          <hr className="article__rule" />
        </div>
      </header>

      <div className="container container--narrow article__body">
        <p>A pilot is supposed to answer a question. Too many of them do not.</p>
        <p>
          They run for six weeks, consume real effort from real people, and end somewhere unhelpful: "it sort of worked," "the team liked it," "hard to say, but probably." That is not an answer. It is a shrug with a project plan attached, and it leaves you exactly where you started, except later and more tired.
        </p>
        <p>
          The good news is that this is almost never a technology problem. A pilot that cannot produce a decision was a pilot that was never designed to. The decision is won or lost in the setup, before any document is processed. So this is a playbook for that setup, and for the four to six weeks that follow.
        </p>

        <h2 className="article__sub">The work that happens before week one</h2>
        <p>Most of what makes a pilot conclusive happens before it starts. Five things, and none of them are optional.</p>
        <ul className="article__list">
          <li><strong>Pick two or three document types, honestly.</strong> Not the cleanest ones, and not the rarest. The ones that genuinely cost you time, and that look like the documents you actually receive. Piloting on hand-picked, tidy documents tells you about a world you do not live in.</li>
          <li><strong>Write your success criterion as a number.</strong> Before the pilot runs, decide what result would make you say yes. Something like: removes at least this share of manual entry time on these document types, while holding errors at or below where they are now. If you cannot write the number, you will not be able to read the result.</li>
          <li><strong>Measure your baseline.</strong> You cannot prove improvement against a number you never took. Time per document, rework rate, cost per batch. Rough is fine. A rough baseline beats no baseline by a wide margin.</li>
          <li><strong>Name the owner and the room.</strong> One person owns the decision. Decide now who else needs to be in the room when it is made, so that the pilot does not simply end and hang there unresolved.</li>
          <li><strong>Gather real documents, including the ugly ones.</strong> The smudged scan, the three-in-one PDF, the vendor whose layout makes no sense. A pilot fed only clean inputs is not a pilot, it is a demo.</li>
        </ul>
        <p>Get these five right and the pilot is already most of the way to being useful. Get them wrong, and no amount of running time will rescue it.</p>

        <h2 className="article__sub">Weeks one and two: train and configure</h2>
        <p>With the setup done, the first phase is teaching the tool your documents.</p>
        <p>
          This is where you train models on the real examples you gathered, set the fields you actually care about, choose the confidence threshold, and decide how flagged work is routed. It is hands-on, and it should be. The question this phase answers is a simple one: can this tool be taught your documents at all. If it cannot be configured to your reality in two weeks of honest effort, that is itself a finding.
        </p>

        <h2 className="article__sub">Weeks three and four: run it on real volume</h2>
        <p>Now you run it for real.</p>
        <p>
          Not a single curated batch, but your actual, ongoing document flow, at something close to real volume. Let the review loop run. Let your team make corrections. This phase is where the pilot earns its keep, because it is the only part that tells you the thing you actually need to know: how much human time the tool removes, and whether the output is trustworthy enough to rely on.
        </p>
        <p>
          Watch the numbers you said you would watch. Time per document against your baseline. The share of fields going to review. The error rate. And one more that matters more than people expect: whether the review rate is trending down as the model learns from your corrections. A flat review rate and a falling one point to very different futures.
        </p>

        <h2 className="article__sub">Weeks five and six: measure and decide</h2>
        <p>The last phase is the one pilots most often skip, which is why so many of them end in a shrug.</p>
        <p>
          Put the results next to the baseline you measured and the criterion you wrote down. Did it clear the number, or not. Look at the trend as well as the snapshot: a tool that finishes the pilot at the criterion with its curve still climbing is in a stronger position than one that hit the number and flattened. Then make the call. Yes or no, the number that justifies it, on the record, with the people you named in the room.
        </p>
        <p>
          If the answer is yes, production is the next conversation: integration, scale, rollout. That is real work, and it is its own scoping exercise. It is not part of the pilot, and trying to fold it in is one of the surest ways to make a pilot sprawl until it decides nothing.
        </p>

        <Planner />

        <h2 className="article__sub">What a pilot cannot tell you</h2>
        <p>Honesty about the limits is part of running a pilot well.</p>
        <p>
          A pilot tells you about the document types you put into it, and nothing about the ones you left out. Six weeks is enough to see a trajectory, not a final state: the accuracy curve will still be climbing when the pilot ends, so you are judging a slope, not an endpoint. And a pilot is not a production system. It shows you whether the thing works on your documents and removes real time. It does not, by itself, deliver integration, scale or change management.
        </p>
        <p>
          There is one more thing worth saying plainly. If a well-run pilot ends in a no, that is not a failed pilot. It is a successful one. The whole point of spending four to six weeks is to buy a clear answer cheaply, and a cheap, well-evidenced no is worth far more than an expensive yes that you backed into because nobody wrote the criterion down.
        </p>

        {/* Closing CTA */}
        <section className="article__cta" data-testid="article-cta">
          <div>
            <h3>Run your pilot with Docket.</h3>
            <p>A Docket pilot follows exactly this shape: four to six weeks, two or three of your real document types, a baseline at the start and an honest number at the end.</p>
          </div>
          <Link to="/contact" className="article__cta-btn" data-testid="article-cta-btn">
            Request a pilot <Arr />
          </Link>
        </section>

        <div className="article__backwrap">
          <Link to="/insights" className="txtlink" data-testid="article-back">
            <svg className="arr" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ transform: "rotate(180deg)" }}>
              <path d="M3 8h9m0 0L8 4m4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to all Insights
          </Link>
        </div>
      </div>
    </article>
  );
}
